import React, { JSX, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { Button, IconName, LinkButton } from "@fidelix/fx-miranda";

import BasicDetails from "../../common/basic-details";
import InfoList from "../../common/info-list";
import Loader from "../../common/loader";
import ConfirmationModal from "../../common/confirmation-modal";
import useToastManager from "../../hooks/toast-manager";
import { formatPhoneNumber } from "../../helper";
import { CompanyContactService } from "../../services/company-contact-service";
import { Contact } from "../../dtos/contact.model";
import StatusBadge from "../../common/status-badge";

interface ContactInfoViewProps {
  id: string;
  service: CompanyContactService;
  redirectUrl: string;
  navigateBackLabel?: string;
  showActions?: boolean;
}

interface IConfirmationInfo {
  label: string;
  icon: IconName;
  buttonLabel: string;
  dataTestId?: string;
}

const ContactInfoView = ({
  id,
  service,
  redirectUrl,
  navigateBackLabel,
  showActions = true,
}: ContactInfoViewProps) => {
  const navigate = useNavigate();
  const { companyId, contactId } = useParams();
  const { successToast, errorToast } = useToastManager();
  const [modal, setModal] = useState<JSX.Element | undefined>(undefined);
  const queryClient = useQueryClient();

  const { data: contact = {} as Contact, isLoading } = useQuery({
    queryKey: ["get-contact", id],
    queryFn: () => service.getOne(+id),
  });
  const contactLabel =
    contact.email ||
    (contact.phone ? formatPhoneNumber(contact.phone) : t`Contact`);

  const PANELS = [
    {
      header: t`Contact Info`,
      rows: [
        { label: t`Email`, value: contact.email || "-" },
        {
          label: t`Phone`,
          value: contact.phone ? formatPhoneNumber(contact.phone) : "-",
        },
      ],
    },
  ];

  const confirmationInfo = (row: Contact): IConfirmationInfo => {
    if (row.active) {
      return {
        icon: "deactive",
        buttonLabel: t`Deactivate contact`,
        label: t`When you deactivate a contact, that contact can no longer use the roles authorized by the company. You can reactivate the contact at any time and enable the use of the services again.`,
        dataTestId: "deactive-button",
      };
    }
    return {
      icon: "plus",
      buttonLabel: t`Activate contact`,
      label: t`When you reactivate the contact, the contact's roles come into effect again and contact can use the services authorized by this company.`,
      dataTestId: "plus-button",
    };
  };

  const { label, icon, buttonLabel, dataTestId } = confirmationInfo(contact);

  const onDelete = () => {
    setModal(
      <ConfirmationModal
        icon="trash"
        onConfirm={mutateOnDelete}
        setShowModal={() => setModal(undefined)}
        message={t`You're about to remove ${contactLabel} from this company. This action cannot be undone.`}
      />,
    );
  };

  const onDeactivate = () => {
    setModal(
      <ConfirmationModal
        icon={icon}
        onConfirm={onUpdate}
        setShowModal={() => setModal(undefined)}
        message={label}
      />,
    );
  };

  const { mutate } = useMutation({
    mutationFn: (data: Contact) => service.update(+contactId, data),
    onSuccess: () => {
      successToast(
        t`Contact ${contact.active ? "deactivated" : "activated"} successfully.`,
      );
      setModal(undefined);
      queryClient.invalidateQueries({ queryKey: ["get-contact"] });
    },
    onError: () => errorToast(t`Failed to update contact.`),
  });

  const onUpdate = () => {
    mutate(new Contact({ ...contact, active: !contact.active }));
  };

  const { mutate: mutateOnDelete, isPending } = useMutation({
    mutationFn: () => service.delete(+id),
    onSuccess: () => {
      successToast(t`Contact was deleted successfully.`);
      navigate(redirectUrl);
    },
    onError: () => {
      errorToast(t`Contact deletion failed.`);
    },
  });

  const goToContacts = () => {
    navigate(redirectUrl);
  };

  if (isLoading) return <Loader />;

  return (
    <>
      <BasicDetails
        title={contactLabel}
        description={contact.email}
        navigateLabel={navigateBackLabel || t`Back to contacts`}
        onNavigateBack={goToContacts}
      >
        <StatusBadge active={contact.active} />
      </BasicDetails>
      {modal}
      <InfoList
        panels={PANELS}
        actions={
          showActions ? (
            <>
              <LinkButton
                icon="edit"
                iconPlacement="left"
                variant="secondary"
                to={`/${companyId}/contacts/${id}/edit/info`}
              >
                <Trans>Edit contact Info</Trans>
              </LinkButton>
              <Button
                icon={icon}
                data-test-id={dataTestId}
                isLoading={isLoading}
                isDisabled={isPending}
                iconPlacement="left"
                variant="secondary"
                onPress={onDeactivate}
              >
                <Trans>{buttonLabel}</Trans>
              </Button>
              <Button
                icon="trash"
                iconPlacement="left"
                variant="secondary"
                onPress={onDelete}
              >
                <Trans>Remove contact</Trans>
              </Button>
            </>
          ) : undefined
        }
      />
    </>
  );
};

export default ContactInfoView;
